import React from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './auth/AuthProvider';
import { LangProvider } from './i18n/LangProvider';
import { App } from './App';
import { ApiError } from './api/client';
import { toast, ToastProvider } from './ui/toast';
import { ConfirmProvider } from './ui/confirm';
import { applyTweaks, loadTweaks } from './ui/tweaks';
// Shared visual language; the theme attribute below drives its colour variables.
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
  // Any create/update/delete that rejects surfaces a toast, so failed actions are
  // never silent. 401 is skipped: the API client already renews or logs the user out.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (error instanceof ApiError && error.status === 401) return;
      const message = error instanceof Error ? error.message : 'Operazione non riuscita.';
      toast.error(message);
    },
  }),
});

// Apply the saved theme before first paint; accent and density stay on the shared defaults.
applyTweaks(loadTweaks());

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <LangProvider>
        <ToastProvider>
          <ConfirmProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </ConfirmProvider>
        </ToastProvider>
      </LangProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
